import {
    ArrowBack,
    ArrowForward,
    FirstPage as FirstPageIcon,
    ImageNotSupported as ImageNotSupportedIcon,
    Refresh as RefreshIcon,
    ZoomIn,
    ZoomOut,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { handleCaretPreservingInput, useCaretPreservingHtmlSync } from '../../hooks/useCaretPreservingContentEditable';
import idmlServiceInstance from '../../services/IdmlService';

const BASE_PAGE_RENDER_WIDTH = 620; // px at zoom=1 -- canvases scale to this width regardless of actual point size
const DEFAULT_FONT_SIZE_PT = 10; // fallback when a run carries no PointSize attribute
const FONT_STACK = 'Georgia, "Times New Roman", serif'; // last-resort default when no real font file is available at all

/**
 * Module-level caches (shared for the life of the tab, not per page-spread mount) so switching
 * pages/languages doesn't refetch the same catalog listing or font bytes repeatedly.
 *
 * Fonts weren't previously loaded into the browser at all: `GET /fonts/catalog` was fetched only
 * to populate the override dropdown in IdmlLanguageManager.js, and the curated Noto/uploaded
 * "Document Fonts" bytes uploaded server-side (design doc Milestones 5/6) were only ever consumed
 * by IdmlCopyFitService/IdmlExportService -- never registered as an in-browser `@font-face`. Every
 * run's `fontFamily` CSS was therefore always resolving to the generic FONT_STACK fallback,
 * regardless of the book's real typography. This registers real `@font-face` rules for whichever
 * families are actually in use on the current page, from whichever real font file is available
 * (an uploaded Document Font, or the curated catalog for a script-substituted segment) -- a real
 * InDesign font that's neither uploaded nor in the catalog has no file to load anywhere, and stays
 * on the FONT_STACK fallback; that's a real, unavoidable limit (see the design doc's own "no real
 * font binaries" call-outs), not something this fixes.
 */
let fontCatalogFamiliesPromise = null;
const catalogFontBlobUrlCache = new Map(); // family (lowercased) -> Promise<blobUrl|null>

const getCatalogFamilies = () => {
    if (!fontCatalogFamiliesPromise) {
        fontCatalogFamiliesPromise = idmlServiceInstance
            .getFontCatalog()
            .then((entries) => new Set((entries || []).map((e) => (e.family || '').toLowerCase())))
            .catch(() => new Set());
    }
    return fontCatalogFamiliesPromise;
};

const getCatalogFontBlobUrl = (family) => {
    const key = family.toLowerCase();
    if (!catalogFontBlobUrlCache.has(key)) {
        catalogFontBlobUrlCache.set(
            key,
            idmlServiceInstance.getFontCatalogFileBlobUrl(family).catch(() => null),
        );
    }
    return catalogFontBlobUrlCache.get(key);
};

/** Every distinct AppliedFont family actually referenced on this page, source and target alike. */
const collectFontFamilies = (layout) => {
    const families = new Set();
    (layout?.frames || []).forEach((frame) => {
        if (frame.frameType === 'IMAGE') return;
        (frame.segments || []).forEach((segment) => {
            const sourceFamily = segment.sourceRunStructure?.runs?.[0]?.fontFamily;
            if (sourceFamily) families.add(sourceFamily);
            const targetFamily = segment.resolvedFontFamily || segment.translationRunStructure?.runs?.[0]?.fontFamily;
            if (targetFamily) families.add(targetFamily);
        });
    });
    return families;
};

/**
 * Resolves the current page's font families against whatever real font bytes are actually
 * available (uploaded Document Fonts first, then the curated catalog) and returns a CSS string of
 * `@font-face` rules to render into a `<style>` tag.
 */
const useIdmlFontFaces = (layout, uploadedFonts) => {
    const [fontFaceCss, setFontFaceCss] = useState('');

    useEffect(() => {
        let cancelled = false;
        const families = collectFontFamilies(layout);
        if (families.size === 0) {
            setFontFaceCss('');
            return undefined;
        }
        const uploadedByFamily = new Map((uploadedFonts || []).map((f) => [(f.family || '').toLowerCase(), f.blobUrl]));

        (async () => {
            const catalogFamilies = await getCatalogFamilies();
            const rules = [];
            for (const family of families) {
                const key = family.toLowerCase();
                const uploadedUrl = uploadedByFamily.get(key);
                if (uploadedUrl) {
                    rules.push(`@font-face { font-family: "${family}"; src: url("${uploadedUrl}"); }`);
                    continue;
                }
                if (catalogFamilies.has(key)) {
                    const blobUrl = await getCatalogFontBlobUrl(family);
                    if (blobUrl) {
                        rules.push(`@font-face { font-family: "${family}"; src: url("${blobUrl}"); }`);
                    }
                }
            }
            if (!cancelled) setFontFaceCss(rules.join('\n'));
        })();

        return () => {
            cancelled = true;
        };
    }, [layout, uploadedFonts]);

    return fontFaceCss;
};

/** True InDesign point size for a run, falling back to a sane body-text default when unset. */
const runFontSizePt = (run) => {
    const raw = run?.attributes?.PointSize;
    const parsed = raw != null ? parseFloat(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : DEFAULT_FONT_SIZE_PT;
};

/**
 * True InDesign leading (line-height) in points for a run. IDML's `Leading` attribute is either a
 * point value or the literal "Auto" (InDesign's default of ~120% of point size) -- either way, a
 * flat CSS ratio like 1.35 doesn't match what InDesign actually used to size the frame, which is
 * why frames can appear to clip even for untouched source text.
 */
const runLeadingPt = (run) => {
    const raw = run?.attributes?.Leading;
    const parsed = raw != null ? parseFloat(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : runFontSizePt(run) * 1.2;
};

const runLineHeightPx = (run, scale) => `${Math.max(6, runLeadingPt(run) * scale)}px`;

const runFontWeight = (run) => {
    const style = (run?.attributes?.FontStyle || '').toLowerCase();
    return style.includes('bold') ? 700 : 400;
};

const runFontStyle = (run) => {
    const style = (run?.attributes?.FontStyle || '').toLowerCase();
    return style.includes('italic') || style.includes('oblique') ? 'italic' : 'normal';
};

const hasRunText = (runStructure) =>
    (runStructure?.runs || []).some((run) => (run.children || []).some((child) => child.kind === 'text' && child.text));

/** Renders a segment's runs with each run's own font family/size/weight/style, instead of flattening to plain text. */
const RichSegmentText = ({ runStructure, plainText, scale }) => {
    if (!hasRunText(runStructure)) {
        return plainText ? <span>{plainText}</span> : null;
    }
    return runStructure.runs.map((run, runIdx) => (
        <span
            key={runIdx}
            style={{
                fontFamily: run.fontFamily ? `"${run.fontFamily}", ${FONT_STACK}` : FONT_STACK,
                fontSize: `${Math.max(6, runFontSizePt(run) * scale)}px`,
                lineHeight: runLineHeightPx(run, scale),
                fontWeight: runFontWeight(run),
                fontStyle: runFontStyle(run),
            }}
        >
            {(run.children || []).map((child, childIdx) => {
                if (child.kind === 'lineBreak') return <br key={childIdx} />;
                if (child.kind === 'text') return <span key={childIdx}>{child.text}</span>;
                return null;
            })}
        </span>
    ));
};

/**
 * Renders a run structure as editable HTML (<b>/<i> spans) for the contentEditable target, per
 * design doc §7's "map inline bold/italic marks to/from runStructure on save". Kept intentionally
 * simple: only bold/italic survive the round trip through contentEditable HTML (footnotes/anchors
 * aren't editable here at all -- see htmlToRunStructure below).
 */
const runStructureToEditableHtml = (runStructure, plainText) => {
    if (!hasRunText(runStructure)) {
        return escapeHtml(plainText || '');
    }
    return runStructure.runs
        .map((run) => {
            const text = (run.children || [])
                .map((child) => {
                    if (child.kind === 'lineBreak') return '<br/>';
                    if (child.kind === 'text') return escapeHtml(child.text || '');
                    return '';
                })
                .join('');
            const bold = runFontWeight(run) === 700;
            const italic = runFontStyle(run) === 'italic';
            let wrapped = text;
            if (italic) wrapped = `<i>${wrapped}</i>`;
            if (bold) wrapped = `<b>${wrapped}</b>`;
            return wrapped;
        })
        .join('');
};

const escapeHtml = (s) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Parses the contentEditable element's current DOM back into an IdmlRunStructure (design doc
 * §7/§1) by walking its child nodes and tracking bold/italic ancestry (<b>/<strong>/<i>/<em>).
 * Consecutive text with identical bold/italic state is merged into a single run; each distinct
 * combination becomes its own run so the server can carry it through to runStructure/export.
 * `formatTemplate` supplies the font family/point-size attributes every freshly-built run should
 * inherit (same convention as IdmlRunStructure.singleRun server-side).
 */
const htmlToRunStructure = (containerEl, formatTemplate) => {
    const runs = [];
    let current = null;

    const flush = () => {
        if (current && current.text.length > 0) {
            runs.push(current);
        }
        current = null;
    };

    const walk = (node, bold, italic) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (!text) return;
            if (!current || current.bold !== bold || current.italic !== italic) {
                flush();
                current = { bold, italic, text: '' };
            }
            current.text += text;
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.nodeName.toUpperCase();
        if (tag === 'BR') {
            flush();
            runs.push({ bold, italic, text: '', lineBreak: true });
            return;
        }
        const nextBold = bold || tag === 'B' || tag === 'STRONG';
        const nextItalic = italic || tag === 'I' || tag === 'EM';
        node.childNodes.forEach((child) => walk(child, nextBold, nextItalic));
    };

    containerEl.childNodes.forEach((child) => walk(child, false, false));
    flush();

    if (runs.length === 0) {
        runs.push({ bold: false, italic: false, text: '' });
    }

    const styleFor = (bold, italic) => {
        if (bold && italic) return 'Bold Italic';
        if (bold) return 'Bold';
        if (italic) return 'Italic';
        return 'Regular';
    };

    return {
        runs: runs.map((r) => ({
            characterStyleId: null,
            fontFamily: formatTemplate?.fontFamily,
            attributes: { ...(formatTemplate?.attributes || {}), FontStyle: styleFor(r.bold, r.italic) },
            children: r.lineBreak ? [{ kind: 'lineBreak' }] : [{ kind: 'text', text: r.text }],
        })),
    };
};

const flattenRunStructureText = (runStructure) =>
    (runStructure?.runs || [])
        .map((run) => (run.children || []).map((c) => (c.kind === 'text' ? c.text : '')).join(''))
        .join('');

/** CSS for a segment's stored copyFitAdjustment (design doc's "Overflow / copy-fit display" §7). */
const copyFitStyle = (copyFitAdjustment, baseLineHeightPx) => {
    if (!copyFitAdjustment) return {};
    const { trackingDelta = 0, leadingReductionPct = 0, horizontalScalePct = 100 } = copyFitAdjustment;
    const style = {};
    if (trackingDelta) {
        style.letterSpacing = `${trackingDelta / 1000}em`; // InDesign tracking: 1/1000 em units
    }
    if (leadingReductionPct) {
        const basePx = parseFloat(baseLineHeightPx) || 0;
        style.lineHeight = `${basePx * (1 - leadingReductionPct / 100)}px`;
    }
    if (horizontalScalePct && horizontalScalePct !== 100) {
        style.display = 'inline-block';
        style.transform = `scaleX(${horizontalScalePct / 100})`;
        style.transformOrigin = 'left';
    }
    return style;
};

/** One editable (or read-only) paragraph in the target panel. */
const EditableSegment = ({ segment, bookId, languageCode, readOnly, scale, onSegmentUpdate, isActiveFrame, onFocusFrame }) => {
    const editorRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [refitting, setRefitting] = useState(false);
    const html = runStructureToEditableHtml(segment.translationRunStructure, segment.translationText);
    const formatRun = segment.translationRunStructure?.runs?.[0] || segment.sourceRunStructure?.runs?.[0];
    const lineHeightPx = runLineHeightPx(formatRun, scale);
    // segment.resolvedFontFamily (server-resolved script-fallback family, per design doc §Milestone
    // 4's "fontFallback boolean only, name re-derived at read-time") takes priority over the run's
    // own AppliedFont -- a fallback segment's runStructure still names the *source* font, which is
    // exactly the wrong one to render translated non-Latin-script text in.
    const editableFontFamily = segment.resolvedFontFamily || formatRun?.fontFamily;

    useCaretPreservingHtmlSync(editorRef, html);

    const commit = async () => {
        if (!editorRef.current) return;
        const formatTemplate = segment.translationRunStructure?.runs?.[0] || segment.sourceRunStructure?.runs?.[0];
        const runStructure = htmlToRunStructure(editorRef.current, formatTemplate);
        const text = flattenRunStructureText(runStructure);
        const currentText = segment.translationText || '';
        if (text === currentText && runStructure.runs.length <= 1) {
            return;
        }
        setSaving(true);
        try {
            const updated = await idmlServiceInstance.patchSegment(bookId, segment.id, {
                languageCode,
                text,
                runStructure: runStructure.runs.length > 1 ? runStructure : undefined,
                saveType: 'MANUAL',
            });
            onSegmentUpdate?.(segment.id, updated);
        } catch (err) {
            console.error('Failed to save page-spread segment edit:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleRefit = async (e) => {
        e.stopPropagation();
        setRefitting(true);
        try {
            const updated = await idmlServiceInstance.refitSegment(bookId, segment.id, languageCode);
            onSegmentUpdate?.(segment.id, updated);
        } catch (err) {
            console.error('Failed to refit segment:', err);
        } finally {
            setRefitting(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                mb: 1,
                p: 0.25,
                borderRadius: 0.5,
                outline: isActiveFrame ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: 1,
            }}
            onFocus={() => onFocusFrame?.(segment.frameId)}
            onClick={() => onFocusFrame?.(segment.frameId)}
        >
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Box
                    ref={editorRef}
                    component="div"
                    contentEditable={!readOnly}
                    suppressContentEditableWarning
                    tabIndex={readOnly ? undefined : segment.order}
                    onInput={(event) => handleCaretPreservingInput(event, () => {})}
                    onBlur={commit}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        lineHeight: lineHeightPx,
                        wordBreak: 'break-word',
                        fontFamily: editableFontFamily ? `"${editableFontFamily}", ${FONT_STACK}` : FONT_STACK,
                        fontSize: `${Math.max(6, runFontSizePt(formatRun) * scale)}px`,
                        color: segment.needsReview ? 'warning.main' : 'text.primary',
                        outline: 'none',
                        cursor: readOnly ? 'default' : 'text',
                        '&:focus': readOnly ? {} : { boxShadow: 'inset 0 0 0 1px', boxShadowColor: 'primary.main' },
                        ...copyFitStyle(segment.copyFitAdjustment, lineHeightPx),
                    }}
                />
                {!readOnly && (
                    <Tooltip title="Re-fit this segment">
                        <span>
                            <IconButton size="small" disabled={refitting} onClick={handleRefit} sx={{ p: 0.25 }}>
                                {refitting ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                {segment.overflow && (
                    <Tooltip title="Estimated to overflow its frame">
                        <Chip label="Overflow" size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                    </Tooltip>
                )}
                {segment.fontFallback && (
                    <Tooltip title={segment.fontFallbackDetails || 'Font substituted for script coverage'}>
                        <Chip label="Font" size="small" color="info" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                    </Tooltip>
                )}
                {saving && <Chip label="Saving..." size="small" variant="outlined" sx={{ height: 16, fontSize: 10 }} />}
            </Stack>
        </Box>
    );
};

const MIN_AUTO_FIT_SCALE = 0.55; // don't shrink text past ~55% -- keeps the preview legible even for badly-oversized frames
const AUTO_FIT_STEP = 0.05;

/**
 * Client-side-only cosmetic shrink (design ask: "the font can be scaled down across the source
 * and target" for text that overflows its frame in the preview -- e.g. a title whose word wraps
 * differently than InDesign's own line composer did, splitting a word or forcing an extra line).
 * Never touches export data or the server-side copy-fit adjustment (IdmlCopyFitService), which by
 * design never changes point size -- this is purely "make the preview look closer to the real
 * typeset page," recomputed fresh whenever the rendered content or target height changes.
 */
const useAutoFitFontScale = (contentRef, targetHeightPx, contentKey) => {
    const [fitScale, setFitScale] = useState(1);

    useEffect(() => {
        setFitScale(1);
    }, [contentKey, targetHeightPx]);

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el || !targetHeightPx) return;
        if (el.scrollHeight > targetHeightPx + 1 && fitScale > MIN_AUTO_FIT_SCALE) {
            setFitScale((prev) => Math.max(MIN_AUTO_FIT_SCALE, prev - AUTO_FIT_STEP));
        }
    }, [fitScale, targetHeightPx, contentKey]);

    return fitScale;
};

const Frame = ({
    frame,
    scale,
    pageWidthPt,
    pageHeightPt,
    useTranslation,
    bookId,
    languageCode,
    readOnly,
    onSegmentUpdate,
    activeFrameId,
    onFocusFrame,
}) => {
    const pref = frame.framePreference || {};
    const segments = frame.segments || [];
    const isActive = activeFrameId === frame.frameId;
    const contentRef = useRef(null);
    const targetHeightPx = frame.height * scale;
    const contentKey = useMemo(
        () => segments.map((s) => (useTranslation ? s.translationText : s.sourceText) || '').join(''),
        [segments, useTranslation],
    );
    const fitScale = useAutoFitFontScale(contentRef, targetHeightPx, contentKey);
    const textScale = scale * fitScale;

    if (frame.frameType === 'IMAGE') {
        // A master-spread background image is commonly authored wider/taller than a single page
        // (a deliberate full-bleed InDesign convention -- the excess is meant to run off the page
        // trim and get cropped at print time), so its stored x/y/width/height can fall outside
        // [0, pageWidthPt]/[0, pageHeightPt]. PageCanvas's outer container can't clip that itself
        // (it stays overflow:'visible' so undersized TEXT frame estimates can still show their
        // last line, see that comment below) -- so an oversized image frame clips itself here
        // instead: an outer viewport box sized to its on-page portion only, with the full image
        // positioned inside it at its true offset, instead of shrinking the whole image to fit.
        const clampedLeft = Math.max(0, frame.x);
        const clampedTop = Math.max(0, frame.y);
        const clampedRight = Math.min(frame.x + frame.width, pageWidthPt);
        const clampedBottom = Math.min(frame.y + frame.height, pageHeightPt);
        const viewportWidth = Math.max(0, clampedRight - clampedLeft);
        const viewportHeight = Math.max(0, clampedBottom - clampedTop);

        return (
            <Box
                sx={{
                    position: 'absolute',
                    left: clampedLeft * scale,
                    top: clampedTop * scale,
                    width: viewportWidth * scale,
                    height: viewportHeight * scale,
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    boxShadow: isActive ? '0 0 0 2px rgba(25,118,210,0.35)' : 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                }}
                onMouseEnter={() => onFocusFrame?.(frame.frameId)}
            >
                {frame.imageAvailable ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: (frame.x - clampedLeft) * scale,
                            top: (frame.y - clampedTop) * scale,
                            width: frame.width * scale,
                            height: frame.height * scale,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={frame.imageUrl}
                            alt=""
                            // The dev server sets Cross-Origin-Embedder-Policy: require-corp (for the video
                            // editor's ffmpeg.wasm). Without crossOrigin, this <img> loads in no-cors mode and
                            // COEP blocks it even though Azure Blob already sends Access-Control-Allow-Origin: *;
                            // crossOrigin="anonymous" makes the browser issue a real CORS request instead.
                            crossOrigin="anonymous"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                    </Box>
                ) : (
                    <Stack
                        alignItems="center"
                        gap={0.5}
                        sx={{ color: 'text.secondary', p: 1, width: '100%', height: '100%', justifyContent: 'center' }}
                    >
                        <ImageNotSupportedIcon fontSize="small" />
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>
                            {frame.vectorOnly ? 'Vector graphic — not previewed' : 'Image unavailable'}
                        </Typography>
                    </Stack>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                left: frame.x * scale,
                top: frame.y * scale,
                width: frame.width * scale,
                // minHeight, not a fixed height: the browser's fallback font (a real InDesign font
                // like "Ek Mukta"/"Acryle Script Personal Use" isn't loaded unless it's an uploaded
                // Document Font or in the curated catalog, see useIdmlFontFaces above) renders text
                // taller than InDesign's own metrics did when it originally fit this frame's height --
                // pinning height to the IDML-stored value left overflow hanging below the frame's own
                // border, detached from it, once clipping was removed (see git history for the prior
                // overflow:hidden/visible back-and-forth here). Letting the box grow to its actual
                // rendered content keeps the border wrapped around the text instead.
                minHeight: frame.height * scale,
                border: frame.needsReview ? '1px dashed' : '1px solid',
                borderColor: frame.needsReview ? 'warning.main' : isActive ? 'primary.main' : 'divider',
                boxShadow: isActive ? '0 0 0 2px rgba(25,118,210,0.35)' : 'none',
                boxSizing: 'border-box',
                overflow: 'visible',
                paddingTop: (pref.insetTopPt || 0) * scale,
                paddingLeft: (pref.insetLeftPt || 0) * scale,
                paddingBottom: (pref.insetBottomPt || 0) * scale,
                paddingRight: (pref.insetRightPt || 0) * scale,
                columnCount: pref.columnCount > 1 ? pref.columnCount : undefined,
                columnGap: pref.columnCount > 1 ? (pref.columnGutterPt || 0) * scale : undefined,
                bgcolor: 'background.paper',
            }}
            onMouseEnter={() => onFocusFrame?.(frame.frameId)}
        >
            <Box ref={contentRef}>
                {frame.needsReview && segments.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                        Text continues from a previous page — needs manual review
                    </Typography>
                )}
                {segments.map((segment) =>
                    useTranslation ? (
                        <EditableSegment
                            key={segment.id}
                            segment={{ ...segment, frameId: frame.frameId }}
                            bookId={bookId}
                            languageCode={languageCode}
                            readOnly={readOnly}
                            scale={textScale}
                            onSegmentUpdate={onSegmentUpdate}
                            isActiveFrame={isActive}
                            onFocusFrame={onFocusFrame}
                        />
                    ) : (
                        <Typography
                            key={segment.id}
                            component="p"
                            sx={{ lineHeight: 'normal', mb: 1, wordBreak: 'break-word', fontFamily: FONT_STACK }}
                        >
                            {!segment.sourceText && !hasRunText(segment.sourceRunStructure) ? (
                                <em>(empty)</em>
                            ) : (
                                <RichSegmentText runStructure={segment.sourceRunStructure} plainText={segment.sourceText} scale={textScale} />
                            )}
                        </Typography>
                    ),
                )}
            </Box>
        </Box>
    );
};

const PageCanvas = ({
    title,
    layout,
    useTranslation,
    renderWidth,
    bookId,
    languageCode,
    readOnly,
    onSegmentUpdate,
    activeFrameId,
    onFocusFrame,
}) => {
    if (!layout || !layout.width || !layout.height) {
        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No page geometry available.
                </Typography>
            </Box>
        );
    }

    const scale = renderWidth / layout.width;
    const renderHeight = layout.height * scale;

    // No z-order/stacking field survives parsing (see IdmlParsingService: text frames are added to
    // layoutDocs first, then all image frames, then master-background images, regardless of each
    // one's real position in the original spread XML), and Mongo returns them in that same
    // insertion order -- so every image frame would otherwise always paint on top of every text
    // frame on its page. A stable sort placing images first (painted first, i.e. behind) matches
    // this book's actual convention of photos/backgrounds sitting behind readable text.
    const orderedFrames = [...(layout.frames || [])].sort(
        (a, b) => (a.frameType === 'IMAGE' ? 0 : 1) - (b.frameType === 'IMAGE' ? 0 : 1),
    );

    return (
        <Box sx={{ flex: '0 0 auto' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <Box
                sx={{
                    position: 'relative',
                    width: renderWidth,
                    height: renderHeight,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 1,
                    mx: 'auto',
                    // Not 'hidden': a Frame below intentionally uses overflow: 'visible' so a paragraph
                    // whose estimated frame height falls short (see that comment) still shows its last
                    // line instead of it vanishing -- but clipping here at the page boundary undid that
                    // by cutting the same overflowing text off a level higher. Left visible so a frame
                    // near the bottom of the page can still spill its overflow below the page edge.
                    overflow: 'visible',
                }}
            >
                {orderedFrames.map((frame) => (
                    <Frame
                        key={frame.id}
                        frame={frame}
                        scale={scale}
                        pageWidthPt={layout.width}
                        pageHeightPt={layout.height}
                        useTranslation={useTranslation}
                        bookId={bookId}
                        languageCode={languageCode}
                        readOnly={readOnly}
                        onSegmentUpdate={onSegmentUpdate}
                        activeFrameId={activeFrameId}
                        onFocusFrame={onFocusFrame}
                    />
                ))}
            </Box>
        </Box>
    );
};

/**
 * Milestone 3 (design doc §7): contentEditable target frames (run-structure-aware rich editing,
 * bold/italic only), copy-fit CSS rendering, font-fallback badges, frame-linked focus between the
 * source/target panels (not scroll sync -- see design doc's explicit divergence from
 * TextEditor.js), explicit Re-fit (single + bulk) actions. `readOnly` is passed down from
 * IdmlBookEditor's `effectivePermission === 'WRITE'` + active-session-lock gating (same rule
 * IdmlSegmentTable already applies) -- this component never decides that on its own.
 *
 * Zoom/pan: zoom is a simple width-scale control (existing from Milestone 2); "pan" is native
 * browser scrolling within the canvas's overflow container rather than custom drag-to-pan JS --
 * a deliberately simple implementation per the design doc ("doesn't need to be fancy, just
 * functional"), not a full pan/zoom widget.
 */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;

const IdmlPageSpreadEditor = ({
    bookId,
    languageCode,
    pageCount = 1,
    readOnly = false,
    uploadedFonts,
    onSegmentUpdate,
    onSegmentsBulkRefit,
}) => {
    const [pageIndex, setPageIndex] = useState(0);
    const [layout, setLayout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [activeFrameId, setActiveFrameId] = useState(null);
    const [bulkRefitting, setBulkRefitting] = useState(false);
    const [pageJumpValue, setPageJumpValue] = useState(String(pageIndex + 1));
    const fontFaceCss = useIdmlFontFaces(layout, uploadedFonts);

    useEffect(() => {
        setPageJumpValue(String(pageIndex + 1));
    }, [pageIndex]);

    const commitPageJump = () => {
        const parsed = parseInt(pageJumpValue, 10);
        if (Number.isNaN(parsed)) {
            setPageJumpValue(String(pageIndex + 1));
            return;
        }
        const clamped = Math.min(Math.max(parsed, 1), pageCount || 1);
        setPageIndex(clamped - 1);
        setPageJumpValue(String(clamped));
    };

    const loadLayout = () => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        idmlServiceInstance
            .getPageLayout(bookId, pageIndex, languageCode)
            .then((data) => {
                if (!cancelled) setLayout(data);
            })
            .catch((err) => {
                console.error('Failed to load page layout:', err);
                if (!cancelled) setError(err.response?.data?.error || 'Failed to load page layout');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    };

    useEffect(loadLayout, [bookId, pageIndex, languageCode]);

    /** Applies a PATCH/refit response's translation fields onto this page's local layout state,
     * so the visible frame reflects the change immediately without a full re-fetch, and also
     * reports it up so the segment table view (shared state, design doc §7) stays in sync. */
    const handleSegmentUpdate = (segmentId, updatedSegment) => {
        const translation = updatedSegment?.translations?.[languageCode];
        setLayout((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                frames: prev.frames.map((frame) => ({
                    ...frame,
                    segments: frame.segments.map((s) =>
                        s.id === segmentId && translation
                            ? {
                                  ...s,
                                  translationText: translation.text,
                                  translationRunStructure: translation.runStructure,
                                  copyFitAdjustment: translation.copyFitAdjustment,
                                  overflow: translation.overflow,
                                  needsReview: translation.needsReview,
                                  fontFallback: translation.fontFallback,
                                  fontFallbackDetails: translation.fontFallbackDetails,
                              }
                            : s,
                    ),
                })),
            };
        });
        onSegmentUpdate?.(segmentId, updatedSegment);
    };

    const handleRefitAll = async () => {
        setBulkRefitting(true);
        try {
            await idmlServiceInstance.refitBook(bookId, languageCode);
            loadLayout();
            if (onSegmentsBulkRefit) {
                await onSegmentsBulkRefit();
            }
        } catch (err) {
            console.error('Failed to bulk refit book:', err);
        } finally {
            setBulkRefitting(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {fontFaceCss && <style>{fontFaceCss}</style>}
            <Alert severity="warning" sx={{ mb: 2 }}>
                Page Preview is for directional/text-fit checking only and is not a substitute for final layout
                confirmation. It approximates page breaks and text flow to help catch content issues early, but exact
                pagination, typography, and design must be verified in Adobe InDesign before publication.
            </Alert>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Tooltip title="First page">
                    <span>
                        <IconButton size="small" disabled={pageIndex === 0} onClick={() => setPageIndex(0)}>
                            <FirstPageIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton size="small" disabled={pageIndex === 0} onClick={() => setPageIndex((p) => p - 1)}>
                    <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="body2">Page</Typography>
                <TextField
                    size="small"
                    type="number"
                    value={pageJumpValue}
                    onChange={(e) => setPageJumpValue(e.target.value)}
                    onBlur={commitPageJump}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            commitPageJump();
                        }
                    }}
                    inputProps={{ min: 1, max: pageCount || 1, style: { textAlign: 'center', padding: '4px 0' } }}
                    sx={{ width: 64 }}
                />
                <Typography variant="body2">{pageCount ? `of ${pageCount}` : ''}</Typography>
                <IconButton
                    size="small"
                    disabled={pageCount > 0 && pageIndex >= pageCount - 1}
                    onClick={() => setPageIndex((p) => p + 1)}
                >
                    <ArrowForward fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    disabled={zoom <= MIN_ZOOM}
                    onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
                    sx={{ ml: 2 }}
                >
                    <ZoomOut fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                    {Math.round(zoom * 100)}%
                </Typography>
                <IconButton
                    size="small"
                    disabled={zoom >= MAX_ZOOM}
                    onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
                >
                    <ZoomIn fontSize="small" />
                </IconButton>
                {!readOnly && (
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 2 }}
                        startIcon={bulkRefitting ? <CircularProgress size={16} /> : <RefreshIcon />}
                        disabled={bulkRefitting}
                        onClick={handleRefitAll}
                    >
                        Re-fit all
                    </Button>
                )}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" sx={{ overflow: 'auto' }}>
                    <PageCanvas
                        title="Source"
                        layout={layout}
                        useTranslation={false}
                        renderWidth={BASE_PAGE_RENDER_WIDTH * zoom}
                        bookId={bookId}
                        languageCode={languageCode}
                        readOnly
                        activeFrameId={activeFrameId}
                        onFocusFrame={setActiveFrameId}
                    />
                    <PageCanvas
                        title={`Target (${languageCode || '—'})`}
                        layout={layout}
                        useTranslation
                        renderWidth={BASE_PAGE_RENDER_WIDTH * zoom}
                        bookId={bookId}
                        languageCode={languageCode}
                        readOnly={readOnly}
                        onSegmentUpdate={handleSegmentUpdate}
                        activeFrameId={activeFrameId}
                        onFocusFrame={setActiveFrameId}
                    />
                </Stack>
            )}
        </Box>
    );
};

export default IdmlPageSpreadEditor;
